import React, { useState } from "react";
import { jsonSend } from "../utils/http";
import { validateNewPassword } from "../utils/passwordValidation";
import "../styles/forms.css";
import "../styles/profile-form.css";

export type PasswordFormProps = {
    saveUrl: string;          // POST { currentPassword, newPassword }
    useCookies?: boolean;
    onChanged?: () => void | Promise<void>;
    onCancel?: () => void;    // New prop for cancel handler
    skipCurrentPassword?: boolean;  // For first-time password changes via invitation
    showDeleteAccount?: boolean;  // Show delete account link at bottom
    onDeleteAccount?: () => void;  // Handler for delete account
};

export default function PasswordForm({ saveUrl, useCookies = false, onChanged, onCancel, skipCurrentPassword = false, showDeleteAccount = false, onDeleteAccount }: PasswordFormProps) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>("");
    const [ok, setOk] = useState<string>("");

    async function onSave() {
        setError(""); setOk("");

        const trimmedNewPassword = newPassword.trim();
        const trimmedConfirmPassword = confirmPassword.trim();
        const trimmedCurrentPassword = currentPassword.trim();

        // Validate based on whether we need current password or not
        if (skipCurrentPassword) {
            if (!trimmedNewPassword) { setError("Please enter a new password."); return; }
        } else {
            if (!trimmedCurrentPassword || !trimmedNewPassword) { setError("Please fill out all password fields."); return; }
        }

        const passwordError = validateNewPassword(trimmedNewPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (trimmedNewPassword !== trimmedConfirmPassword) { setError("New password and confirmation do not match."); return; }

        // Debug: Log the actual values before sending
        console.log("Password values before sending:", {
            skipCurrentPassword,
            currentPassword: skipCurrentPassword ? 'N/A' : trimmedCurrentPassword,
            newPassword: trimmedNewPassword,
            currentPasswordLength: trimmedCurrentPassword.length,
            newPasswordLength: trimmedNewPassword.length,
            hasUpperCase: /[A-Z]/.test(trimmedNewPassword),
            hasLowerCase: /[a-z]/.test(trimmedNewPassword)
        });

        setSaving(true);
        try {
            // Only include currentPassword in payload if we're not skipping it
            const payload = skipCurrentPassword
                ? { newPassword: trimmedNewPassword }
                : { currentPassword: trimmedCurrentPassword, newPassword: trimmedNewPassword };

            console.log("Payload being sent:", payload);
            console.log("JSON stringified payload:", JSON.stringify(payload));

            await jsonSend(saveUrl, "PATCH", payload, useCookies);
            setOk("Password updated.");
            setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
            await onChanged?.();
        } catch (e: any) {
            setError(e?.message || "Failed to update password.");
        } finally {
            setSaving(false);
        }
    }

    function handleCancel() {
        // Reset form fields
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setError("");
        setOk("");
        // Call the provided onCancel handler (which will navigate to /admin-profile)
        onCancel?.();
    }

    return (
        <section className="card form-card" data-testid="password-form-card">
            <div className="form-head">
                <div>
                    <h2 className="form-title">{skipCurrentPassword ? "Set Your Password" : "Password"}</h2>
                    {skipCurrentPassword && (
                        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                            Please create a new password for your account.
                        </p>
                    )}
                </div>
                <div className="form-head-actions">
                    {onCancel && (
                        <button type="button" className="btn-cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                    )}
                    <button
                        className="btn-pill"
                        onClick={onSave}
                        disabled={saving}
                        aria-busy={saving}
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>

            {(error || ok) && (
                <div className={`form-alert ${error ? "is-error" : "is-ok"}`}>{error || ok}</div>
            )}

            <div className="grid-2">
                {/* Only show current password field if not skipping it */}
                {!skipCurrentPassword && (
                    <>
                        <div className="field">
                            <label>Current password</label>
                            <input
                                className="input"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                autoComplete="current-password"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                            />
                        </div>
                        <div className="field" />
                    </>
                )}
                <div className="field">
                    <label>New password</label>
                    <input
                        className="input"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                </div>
                <div className="field">
                    <label>Confirm password</label>
                    <input
                        className="input"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                </div>
            </div>

            {/* Delete Account Link - shown at bottom like in AddAthleteProfileForm */}
            {showDeleteAccount && onDeleteAccount && (
                <div className="delete-account-row">
                    <button
                        type="button"
                        className="link-danger"
                        onClick={onDeleteAccount}
                    >
                        DELETE ACCOUNT
                    </button>
                    <div></div>
                </div>
            )}
        </section>
    );
}
